import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  FileText, Loader2, Sparkles, Download, ArrowLeft, Mail, Phone, MapPin, Linkedin, Github, GraduationCap, Briefcase, FolderGit2, Award, Lightbulb,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import WeKitLogo from "@/components/WeKitLogo";
import { cloudStorage } from "@/lib/cloudStore";

interface ResumeContent {
  headline: string;
  summary: string;
  skills: { technical: string[]; soft: string[]; tools: string[] };
  experience_bullets: { role: string; org: string; bullets: string[] }[];
  project_bullets: { name: string; description: string; bullets: string[] }[];
  achievements: string[];
  ats_tips: string[];
}

interface Inputs {
  full_name: string;
  email: string;
  phone: string;
  location: string;
  linkedin: string;
  github: string;
  target_career: string;
  education: string;
  experience: string;
  projects: string;
  certifications: string;
}

const EMPTY: Inputs = {
  full_name: "", email: "", phone: "", location: "",
  linkedin: "", github: "", target_career: "",
  education: "", experience: "", projects: "", certifications: "",
};

export default function Resume() {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState<Inputs>(EMPTY);
  const [resume, setResume] = useState<ResumeContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const resumeRef = useRef<HTMLDivElement>(null);

  // Prefill from profile + saved interview career
  useEffect(() => {
    const presetCareer = cloudStorage.getItem("wekit_interview_career") ?? "";
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { data: p } = await supabase.from("profiles").select("full_name, email").eq("id", u.user.id).maybeSingle();
      setInputs((prev) => ({
        ...prev,
        full_name: p?.full_name ?? "",
        email: p?.email ?? u.user!.email ?? "",
        target_career: presetCareer,
      }));
    })();
  }, []);

  const update = (k: keyof Inputs, v: string) => setInputs((p) => ({ ...p, [k]: v }));

  const build = async () => {
    if (!inputs.full_name.trim() || !inputs.target_career.trim()) {
      toast({ title: "Add your name and target career", description: "These are required to build your resume." });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("build-resume", {
        body: {
          target_career: inputs.target_career,
          assessment_id: cloudStorage.getItem("wekit_assessment_id") || undefined,
          inputs,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setResume(data);
      setTimeout(() => resumeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e: any) {
      toast({ title: "Couldn't build resume", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    if (!resumeRef.current) return;
    setExporting(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");
      const canvas = await html2canvas(resumeRef.current, { scale: 2, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`${inputs.full_name.replace(/\s+/g, "-")}-Resume.pdf`);
      toast({ title: "Resume downloaded!" });
    } catch (e: any) {
      toast({ title: "Couldn't export", description: e.message, variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container max-w-5xl flex items-center justify-between py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <WeKitLogo className="h-7" />
          <div className="w-16" />
        </div>
      </header>

      <main className="container max-w-5xl py-8 space-y-6">
        <div className="text-center space-y-2">
          <h1 className="font-display text-3xl sm:text-4xl font-bold gradient-text">AI Resume Builder</h1>
          <p className="text-muted-foreground text-sm max-w-xl mx-auto">
            Fill in your basics. YOMA writes the summary, skills, and impact bullets tailored to your target career.
          </p>
        </div>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="font-display text-xl">Your information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Full name *" value={inputs.full_name} onChange={(v) => update("full_name", v)} />
              <Field label="Target career *" value={inputs.target_career} onChange={(v) => update("target_career", v)} placeholder="e.g. Product Designer" />
              <Field label="Email" value={inputs.email} onChange={(v) => update("email", v)} />
              <Field label="Phone" value={inputs.phone} onChange={(v) => update("phone", v)} />
              <Field label="Location" value={inputs.location} onChange={(v) => update("location", v)} placeholder="Bengaluru, India" />
              <Field label="LinkedIn" value={inputs.linkedin} onChange={(v) => update("linkedin", v)} placeholder="linkedin.com/in/…" />
              <Field label="GitHub / Portfolio" value={inputs.github} onChange={(v) => update("github", v)} />
            </div>

            <BigField
              label="Education"
              value={inputs.education}
              onChange={(v) => update("education", v)}
              placeholder="B.Tech CSE, IIIT Hyderabad (2024-2028), CGPA: 8.6/10"
              rows={3}
            />
            <BigField
              label="Internships / Work experience (optional)"
              value={inputs.experience}
              onChange={(v) => update("experience", v)}
              placeholder="UX Intern @ Zomato (Jun-Aug 2025) — Helped redesign onboarding..."
              rows={4}
            />
            <BigField
              label="Projects"
              value={inputs.projects}
              onChange={(v) => update("projects", v)}
              placeholder="WeKIT™ — built a career assessment app with React + Supabase…"
              rows={4}
            />
            <BigField
              label="Certifications / Awards / Volunteering"
              value={inputs.certifications}
              onChange={(v) => update("certifications", v)}
              placeholder="Google UX Design Certificate, NSS volunteer, hackathon winner…"
              rows={3}
            />

            <Button
              onClick={build}
              disabled={loading}
              size="lg"
              className="w-full rounded-full gap-2 bg-gradient-to-r from-primary to-accent text-primary-foreground"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {resume ? "Regenerate resume" : "Build my resume"}
            </Button>
          </CardContent>
        </Card>

        {loading && !resume && (
          <Card className="border-border/50">
            <CardContent className="p-12 flex flex-col items-center gap-3">
              <Loader2 className="h-7 w-7 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">YOMA is crafting your resume…</p>
            </CardContent>
          </Card>
        )}

        {resume && (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-display text-2xl font-bold">Your Resume</h2>
              <Button onClick={exportPDF} disabled={exporting} className="rounded-full gap-2">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Download PDF
              </Button>
            </div>

            {/* Printable resume */}
            <div
              ref={resumeRef}
              className="bg-white text-slate-900 rounded-xl shadow-lg p-8 sm:p-10 max-w-[820px] mx-auto"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <div className="border-b-2 border-slate-900 pb-4">
                <h1 className="text-3xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {inputs.full_name}
                </h1>
                <p className="text-slate-600 text-sm mt-1">{resume.headline}</p>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-700">
                  {inputs.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{inputs.email}</span>}
                  {inputs.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{inputs.phone}</span>}
                  {inputs.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{inputs.location}</span>}
                  {inputs.linkedin && <span className="flex items-center gap-1"><Linkedin className="h-3 w-3" />{inputs.linkedin}</span>}
                  {inputs.github && <span className="flex items-center gap-1"><Github className="h-3 w-3" />{inputs.github}</span>}
                </div>
              </div>

              <PrintSection title="Summary">
                <p className="text-sm leading-relaxed text-slate-800">{resume.summary}</p>
              </PrintSection>

              <PrintSection title="Skills">
                <div className="grid gap-1 text-sm text-slate-800">
                  <p><span className="font-semibold">Technical:</span> {resume.skills.technical.join(" · ")}</p>
                  <p><span className="font-semibold">Tools:</span> {resume.skills.tools.join(" · ")}</p>
                  <p><span className="font-semibold">Soft skills:</span> {resume.skills.soft.join(" · ")}</p>
                </div>
              </PrintSection>

              {inputs.education && (
                <PrintSection title="Education">
                  <p className="text-sm whitespace-pre-line text-slate-800">{inputs.education}</p>
                </PrintSection>
              )}

              {resume.experience_bullets.length > 0 && (
                <PrintSection title="Experience">
                  {resume.experience_bullets.map((e, i) => (
                    <div key={i} className="mb-3 last:mb-0">
                      <p className="text-sm font-semibold">{e.role} <span className="font-normal text-slate-600">— {e.org}</span></p>
                      <ul className="mt-1 list-disc pl-5 space-y-0.5 text-sm text-slate-800">
                        {e.bullets.map((b, j) => <li key={j}>{b}</li>)}
                      </ul>
                    </div>
                  ))}
                </PrintSection>
              )}

              {resume.project_bullets.length > 0 && (
                <PrintSection title="Projects">
                  {resume.project_bullets.map((p, i) => (
                    <div key={i} className="mb-3 last:mb-0">
                      <p className="text-sm font-semibold">{p.name}</p>
                      <p className="text-xs text-slate-600">{p.description}</p>
                      <ul className="mt-1 list-disc pl-5 space-y-0.5 text-sm text-slate-800">
                        {p.bullets.map((b, j) => <li key={j}>{b}</li>)}
                      </ul>
                    </div>
                  ))}
                </PrintSection>
              )}

              {resume.achievements.length > 0 && (
                <PrintSection title="Achievements">
                  <ul className="list-disc pl-5 space-y-0.5 text-sm text-slate-800">
                    {resume.achievements.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </PrintSection>
              )}
            </div>

            <Card className="border-primary/30 bg-primary/5 max-w-[820px] mx-auto">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 font-display">
                  <Lightbulb className="h-4 w-4 text-primary" /> ATS tips for your resume
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {resume.ats_tips.map((t, i) => (
                    <li key={i} className="flex gap-2 text-foreground">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="text-xs font-medium text-foreground">{label}</label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1" />
    </div>
  );
}

function BigField({ label, value, onChange, placeholder, rows }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }) {
  return (
    <div>
      <label className="text-xs font-medium text-foreground">{label}</label>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows ?? 3} className="mt-1 resize-y" />
    </div>
  );
}

function PrintSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-5">
      <h2 className="text-xs font-bold uppercase tracking-widest text-slate-900 border-b border-slate-300 pb-1 mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        {title}
      </h2>
      {children}
    </section>
  );
}
